import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, RotateCcw, Copy, Check } from 'lucide-react';
import { AdvancedColorPicker } from './AdvancedColorPicker';

interface GradientStop {
  color: string;
  position: number; // 0-100%
}

interface GradientData {
  type: 'linear' | 'radial' | 'conic';
  angle?: number; // for linear gradients (0-360 degrees)
  stops: GradientStop[];
  // Radial gradient specific
  centerX?: number; // 0-100%
  centerY?: number; // 0-100%
  shape?: 'circle' | 'ellipse';
}

interface GradientEditorProps {
  gradient: GradientData;
  onChange: (gradient: GradientData) => void;
  onClose?: () => void;
  className?: string;
}

// Generate CSS gradient string
const generateGradientCSS = (gradient: GradientData): string => {
  const stops = gradient.stops
    .sort((a, b) => a.position - b.position)
    .map(stop => `${stop.color} ${stop.position}%`)
    .join(', ');

  switch (gradient.type) {
    case 'linear':
      return `linear-gradient(${gradient.angle || 0}deg, ${stops})`;
    case 'radial':
      const centerX = gradient.centerX || 50;
      const centerY = gradient.centerY || 50;
      const shape = gradient.shape || 'ellipse';
      return `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stops})`;
    case 'conic':
      const cCenterX = gradient.centerX || 50;
      const cCenterY = gradient.centerY || 50;
      return `conic-gradient(from ${gradient.angle || 0}deg at ${cCenterX}% ${cCenterY}%, ${stops})`;
    default:
      return `linear-gradient(0deg, ${stops})`;
  }
};

export const GradientEditor: React.FC<GradientEditorProps> = ({
  gradient,
  onChange,
  onClose,
  className = ''
}) => {
  const [selectedStopIndex, setSelectedStopIndex] = useState<number | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [copied, setCopied] = useState(false);
  const gradientBarRef = useRef<HTMLDivElement>(null);

  const updateGradient = useCallback((updates: Partial<GradientData>) => {
    onChange({ ...gradient, ...updates });
  }, [gradient, onChange]);

  const addStop = useCallback((position: number) => {
    // Find colors of adjacent stops to interpolate
    const sortedStops = [...gradient.stops].sort((a, b) => a.position - b.position);
    let leftStop = sortedStops[0];
    let rightStop = sortedStops[sortedStops.length - 1];
    
    for (let i = 0; i < sortedStops.length - 1; i++) {
      if (sortedStops[i].position <= position && sortedStops[i + 1].position >= position) {
        leftStop = sortedStops[i];
        rightStop = sortedStops[i + 1];
        break;
      }
    }

    // Simple interpolation - use left stop color for now
    const newStop: GradientStop = {
      color: leftStop.color,
      position: Math.round(position)
    };

    const newStops = [...gradient.stops, newStop];
    updateGradient({ stops: newStops });
    setSelectedStopIndex(newStops.length - 1);
  }, [gradient.stops, updateGradient]);

  const removeStop = useCallback((index: number) => {
    if (gradient.stops.length <= 2) return; // Keep at least 2 stops
    
    const newStops = gradient.stops.filter((_, i) => i !== index);
    updateGradient({ stops: newStops });
    setSelectedStopIndex(null);
  }, [gradient.stops, updateGradient]);

  const updateStop = useCallback((index: number, updates: Partial<GradientStop>) => {
    const newStops = gradient.stops.map((stop, i) => 
      i === index ? { ...stop, ...updates } : stop
    );
    updateGradient({ stops: newStops });
  }, [gradient.stops, updateGradient]);

  const handleGradientBarClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent modal from closing
    const bar = gradientBarRef.current;
    if (!bar) return;

    const rect = bar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = (x / rect.width) * 100;

    addStop(Math.max(0, Math.min(100, position)));
  }, [addStop]);

  const handleStopDrag = useCallback((index: number, e: React.MouseEvent<HTMLDivElement>) => {
    const bar = gradientBarRef.current;
    if (!bar) return;

    const startX = e.clientX;
    const startPosition = gradient.stops[index].position;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = bar.getBoundingClientRect();
      const deltaX = e.clientX - startX;
      const deltaPosition = (deltaX / rect.width) * 100;
      const newPosition = Math.max(0, Math.min(100, startPosition + deltaPosition));
      
      updateStop(index, { position: newPosition });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [gradient.stops, updateStop]);

  const copyGradientCSS = useCallback(async () => {
    try {
      const css = generateGradientCSS(gradient);
      await navigator.clipboard.writeText(css);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy gradient CSS:', err);
    }
  }, [gradient]);

  const gradientCSS = generateGradientCSS(gradient);

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-96 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded" />
          <span className="text-sm font-medium text-gray-700">Gradient Editor</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        )}
      </div>

      {/* Gradient Preview */}
      <div className="mb-4">
        <div
          className="w-full h-20 border border-gray-200 rounded-lg"
          style={{ background: gradientCSS }}
        />
      </div>

      {/* Gradient Type */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 mb-2">Type</label>
        <div className="flex space-x-1">
          {(['linear', 'radial', 'conic'] as const).map((type) => (
            <button
              key={type}
              onClick={(e) => { e.stopPropagation(); updateGradient({ type }); }}
              className={`flex-1 py-2 px-3 text-xs rounded transition-colors ${
                gradient.type === type
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Angle Control (for linear and conic) */}
      {(gradient.type === 'linear' || gradient.type === 'conic') && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Angle: {gradient.angle || 0}°
          </label>
          <input
            type="range"
            min="0"
            max="360"
            value={gradient.angle || 0}
            onChange={(e) => updateGradient({ angle: parseInt(e.target.value) })}
            className="w-full"
          />
        </div>
      )}

      {/* Center Position (for radial and conic) */}
      {(gradient.type === 'radial' || gradient.type === 'conic') && (
        <div className="mb-4 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Center X: {gradient.centerX || 50}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={gradient.centerX || 50}
              onChange={(e) => updateGradient({ centerX: parseInt(e.target.value) })}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Center Y: {gradient.centerY || 50}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={gradient.centerY || 50}
              onChange={(e) => updateGradient({ centerY: parseInt(e.target.value) })}
              className="w-full"
            />
          </div>
        </div>
      )}

      {/* Shape (for radial) */}
      {gradient.type === 'radial' && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700 mb-2">Shape</label>
          <div className="flex space-x-1">
            {(['circle', 'ellipse'] as const).map((shape) => (
              <button
                key={shape}
                onClick={(e) => { e.stopPropagation(); updateGradient({ shape }); }}
                className={`flex-1 py-2 px-3 text-xs rounded transition-colors ${
                  (gradient.shape || 'ellipse') === shape
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {shape.charAt(0).toUpperCase() + shape.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Gradient Stops */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 mb-2">Color Stops</label>
        
        {/* Gradient Bar */}
        <div className="relative mb-3">
          <div
            ref={gradientBarRef}
            className="w-full h-8 border border-gray-200 rounded cursor-pointer"
            style={{ background: gradientCSS }}
            onClick={handleGradientBarClick}
          />
          
          {/* Stop Indicators */}
          {gradient.stops.map((stop, index) => (
            <div
              key={index}
              className={`absolute w-4 h-4 border-2 rounded-full cursor-move transform -translate-x-1/2 -translate-y-1/2 ${
                selectedStopIndex === index
                  ? 'border-blue-500 bg-white shadow-md'
                  : 'border-white bg-gray-300 hover:bg-gray-400'
              }`}
              style={{
                left: `${stop.position}%`,
                top: '50%',
                backgroundColor: selectedStopIndex === index ? stop.color : undefined
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setSelectedStopIndex(index);
                handleStopDrag(index, e);
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStopIndex(index);
                setShowColorPicker(true);
              }}
            />
          ))}
        </div>

        {/* Stop Controls */}
        {selectedStopIndex !== null && (
          <div className="space-y-2 p-3 bg-gray-50 rounded">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">
                Stop {selectedStopIndex + 1}
              </span>
              {gradient.stops.length > 2 && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeStop(selectedStopIndex); }}
                  className="p-1 text-red-500 hover:text-red-700"
                  title="Remove stop"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <div
                className="w-6 h-6 border border-gray-200 rounded cursor-pointer"
                style={{ backgroundColor: gradient.stops[selectedStopIndex].color }}
                onClick={(e) => { e.stopPropagation(); setShowColorPicker(true); }}
              />
              <input
                type="text"
                value={gradient.stops[selectedStopIndex].color}
                onChange={(e) => updateStop(selectedStopIndex, { color: e.target.value })}
                className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded font-mono"
              />
              <span className="text-xs text-gray-500">
                {Math.round(gradient.stops[selectedStopIndex].position)}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={(e) => { e.stopPropagation(); copyGradientCSS(); }}
          className="flex items-center space-x-1 px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
        >
          {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
          <span>{copied ? 'Copied!' : 'Copy CSS'}</span>
        </button>
        
        <div className="text-xs text-gray-500">
          Click on gradient to add stops
        </div>
      </div>

      {/* Color Picker Modal - Rendered as Portal */}
      {showColorPicker && selectedStopIndex !== null && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowColorPicker(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AdvancedColorPicker
              color={gradient.stops[selectedStopIndex].color}
              onChange={(color) => updateStop(selectedStopIndex, { color })}
              onClose={() => setShowColorPicker(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
